import { CoordinatePoint3 } from '../../../spatial-classes';
import { nearestSixteenth } from '../../../helpers/nearest-sixteenth';
import { PanelItem } from '../panel-item.class';

export type MoveParams = Partial<CoordinatePoint3<number>>;

/** Guard flag to prevent infinite recursion during linked group moves */
let isLinkedMove = false;

/** Offset the grabbed item settled on, adopted by every bottom-bound sibling so a fridge and its trim kit share one bottom edge */
let linkedBottomOffsetY: number | null = null;

/** Top-bound items are pinned to the countertop, so they sit still while the rest of the group moves under them. */
const movesVertically = (panelItem: PanelItem): boolean => !panelItem.boundTop;

/**
 * Largest slice of dY the whole group can take without any of its moving items leaving the panel.
 * Clamping the group as a unit -- rather than each item against its siblings -- is what keeps a
 * fridge and its trim kit locked together: they are both bottom-bound, so each used to read the
 * other as its own floor and only the first one in the list could move.
 * @param dY requested vertical delta in inches
 * @param panelItem instance of PanelItem initiating the move
 * @returns the delta to apply to every moving item in the group
 */
const clampVerticalDeltaCfn = (dY: number, panelItem: PanelItem): number => {
	const { panelItemGroup = null } = panelItem.refMap ?? {};
	if (panelItemGroup === null) return dY;

	const movers = panelItemGroup.items.children.filter(movesVertically);
	if (movers.length === 0) return 0;

	const panelHalfSize = (panelItemGroup.refMap?.panel?.size.height ?? 0) / 2;
	const topBoundItem = panelItemGroup.getTopBoundItem();
	const topLimit = topBoundItem ? topBoundItem.center.y + (topBoundItem.size.height / 2) : -panelHalfSize;
	const groupTop = Math.min(...movers.map((mover) => mover.center.y - (mover.size.height / 2)));
	const groupBottom = Math.max(...movers.map((mover) => mover.center.y + (mover.size.height / 2)));

	const headroom = topLimit - groupTop;
	const legroom = panelHalfSize - groupBottom;
	if (headroom > legroom) return 0;

	return nearestSixteenth(Math.max(headroom, Math.min(dY, legroom)));
};

/**
 * Logic for moving Panel Item
 * @param movableDeltas number in inches to move center point by x, y and z
 * @param panelItem instance of PanelItem to be moved
 */
export const moveCfn = (movableDeltas: MoveParams, panelItem: PanelItem): void => {
	const { x: dX = 0, y: dY = 0, z: dZ = 0 } = movableDeltas;
	const { panelItemGroup = null } = panelItem.refMap ?? {};

	if (dX !== 0) {
		if (panelItemGroup === null) panelItem.center.x += dX;
	}

	// the initiating move clamps once for the whole group; the linked pass reuses that delta so siblings cannot drift apart
	const deltaY = isLinkedMove ? dY : clampVerticalDeltaCfn(dY, panelItem);

	if (deltaY !== 0 && movesVertically(panelItem)) {
		// a bottom-bound item's center.y is re-derived on every read, so its move only survives on offset.y
		if (panelItem.boundBottom) panelItem.offset.y = linkedBottomOffsetY ?? nearestSixteenth(panelItem.offset.y + deltaY);
		else panelItem.center.y += deltaY;
	}

	panelItem.center.roundNearestSixteenth();

	panelItem.center.z = dZ;

	// linked group move: move siblings in the same PanelItemGroup by the same delta
	if (!isLinkedMove && panelItemGroup) {
		const { children } = panelItemGroup.items ?? {};
		if (children) {
			const siblings = children.filter((sibling) => sibling !== panelItem);
			if (siblings.length > 0) {
				isLinkedMove = true;
				linkedBottomOffsetY = deltaY !== 0 && panelItem.boundBottom ? panelItem.offset.y : null;
				siblings.forEach((sibling) => { sibling.move({ ...movableDeltas, y: deltaY }); });
				linkedBottomOffsetY = null;
				isLinkedMove = false;
			}
		}
	}
};
